<template>
  <content-dialog
    v-model="dialogOpen"
    :loading="loading"
    width="350px"
    persistent
    class="z-max"
    @hide="$emit('hide')"
  >
    <div class="q-pa-md">
      <div class="column flex flex-center">
        <profile-image
          size="75px"
          class="q-mb-md"
          :profile-image="userData.profile_image"
          :color="userData.color"
          :initials="userData.initials"
        />
        <div class="text-h6">
          {{ userData.name }}
        </div>
      </div>

      <div class="text-center q-my-sm text-grey-7">
        <div v-if="!hasDefaultSlot">
          We require you to verify your current password.
        </div>
        <div v-else>
          <slot />
        </div>
      </div>
      <q-form :autocomplete="!autocomplete ? 'off' : undefined" @submit="verifyPassword">
        <q-password
          v-model="password"
          :error="!!errors['password']"
          :error-message="!!errors['password'] ? errors['password'][0] : null"
          :autocomplete="!autocomplete ? 'false' : 'current-password'"
          autofocus
          name="password"
          outlined
          label="Current Password..."
          hide-bottom-space
          class="q-mb-md"
        />
        <slot name="actions" />
        <div class="text-center flex column q-gutter-sm">
          <q-btn
            type="submit"
            label="Verify"
            color="blue"
            padding="sm"
            :loading="loading"
            :class="{'q-mb-md': !!$slots.buttons}"
          />
          <slot name="buttons" />
        </div>
      </q-form>
    </div>
  </content-dialog>
</template>

<script>
/**
 * This component can be used for prompting the user to enter their password
 * before performing an action
 *
 * EMIT:
 * - submit: if customEndpoint = true
 * - validated: emit once the password has been validated (over default endpoint)
 * - nativePrefilled: if password was preflilled via cordova/capacitor etc
 */

// Store must be manually defined as this component can be used
// outside of the default app scope
import { store } from 'src/store'

export default {
  store,
  props: {
    // Perform the request over a custom endpoint (used alongside emitted submit event)
    customEndpoint: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: Boolean,
      default: false
    }
  },
  emits: ['hide', 'submit', 'validated', 'nativePrefilled'],
  data () {
    return {
      loading: false,
      dialogOpen: false,
      errors: {},
      password: ''
    }
  },
  computed: {
    hasDefaultSlot () {
      return !!this.$slots.default
    }
  },
  methods: {
    async open () {
      this.dialogOpen = true
      this.password = ''
      this.errors = {}

      // If using Capacitor SecureStorage, fetch saved password and prefill box
      if (this.autocomplete && this.$nativeSecureStorage) {
        try {
          const ret = await this.$nativeSecureStorage.get({ key: 'login_details' })
          const value = JSON.parse(ret.value)
          if (value && value instanceof Object && value.email && value.password && this.userData.email === value.email) {
            this.password = value.password
            this.$emit('nativePrefilled')
          }
        }
        catch (err) {
          console.log(err)
        }
      }
    },
    close () {
      this.dialogOpen = false
    },
    async verifyPassword () {
      if (this.customEndpoint) {
        this.$emit(
          'submit',
          {
            loading: () => {
              this.loading = true
            },
            done: () => {
              this.loading = false
            },
            submit: () => {
              return {
                email: this.userData.email,
                password: this.password
              }
            },
            onError: (message, errors = {}) => {
              this.errors = errors
              this.$nextTick(() => { this.scrollToFirstError(this.$el) })
            }
          }
        )
      }
      else {
        this.errors = {}
        this.loading = true
        const { status, errors, payload } = await this.$post('v1/user/password/validate', {
          password: this.password
        })
        this.loading = false
        if (status === 'success') {
          this.$emit('validated', payload.token)
          this.dialogOpen = false
        }
        else {
          this.errors = errors
          this.$nextTick(() => { this.scrollToFirstError(this.$el) })
        }
      }
    }
  }
}
</script>
